import type { AreaCatalog } from '../area';
import type { admin as en } from '../en/admin';

export const admin = {
  // --- Frame ---
  'admin.title': 'Admin',
  'admin.tabs.label': 'Admin-Bereich',
  'admin.tab.usage': 'Nutzung',
  'admin.tab.accounts': 'Konten',
  'admin.tab.links': 'Links',
  'admin.dock.home': 'Startseite',
  'admin.dock.newLink': 'Neuer Link',
  'admin.role.viewer': 'Betrachtende',
  'admin.role.member': 'Mitglied',
  'admin.role.owner': 'Verwaltende',

  // --- The figures over every tab ---
  'admin.figures.label': 'Die Instanz in Zahlen',
  'admin.figures.accounts': 'Konten',
  'admin.figures.accountsHint': 'Jede Person mit einem Konto auf dieser Instanz.',
  'admin.figures.accountsSub': {
    one: '{count} Betreibende · {unverified} nicht bestätigt · {deleted} gelöscht',
    other: '{count} Betreibende · {unverified} nicht bestätigt · {deleted} gelöscht',
  },
  'admin.figures.seenToday': 'heute online',
  'admin.figures.seenTodayHint': 'Konten, deren Sitzung heute genutzt wurde (UTC).',
  'admin.figures.seenTodaySub': '{week} in 7 Tagen · {month} in 30 Tagen',
  'admin.figures.items': 'Aufgaben',
  'admin.figures.itemsHint': 'Aufgaben auf der Instanz, abgeschlossene mitgezählt.',
  'admin.figures.events': 'Ereignisse',
  'admin.figures.eventsHint': 'Jede Aktion im Protokoll, seit es die Instanz gibt.',
  'admin.figures.eventsSub': {
    one: '{events} in {days} Tagen · {count} Anmeldung',
    other: '{events} in {days} Tagen · {count} Anmeldungen',
  },

  // --- Usage ---
  'admin.usage.lastDays': 'Letzte {days} Tage',
  'admin.usage.lastDaysHint':
    'UTC. Online heißt: eine Sitzung wurde genutzt. Aktiv heißt: eine Aktion im Protokoll.',
  'admin.usage.measure.signIns': 'Anmeldungen',
  'admin.usage.measure.events': 'Ereignisse',
  'admin.usage.measure.activeAccounts': 'Aktive Konten',
  'admin.usage.chart.total': '{total} in {count} Tagen',
  'admin.usage.chart.label': '{measure} pro Tag, letzte {count} Tage',
  'admin.usage.bar.signIns': {
    one: '{day}: {count} Anmeldung',
    other: '{day}: {count} Anmeldungen',
  },
  'admin.usage.bar.events': { one: '{day}: {count} Ereignis', other: '{day}: {count} Ereignisse' },
  'admin.usage.bar.activeAccounts': {
    one: '{day}: {count} aktives Konto',
    other: '{day}: {count} aktive Konten',
  },
  'admin.usage.chart.today': 'heute',
  'admin.usage.numbers': 'Zahlen',
  'admin.usage.byDay': 'Nach Tag',
  'admin.usage.table.day': 'Tag',
  'admin.usage.table.active': 'Aktiv',

  // --- Accounts ---
  'admin.accounts.filter': 'Konten filtern',
  'admin.accounts.filterPlaceholder': 'Konten filtern…',
  'admin.accounts.showDeleted': 'Gelöschte zeigen',
  'admin.accounts.hideDeleted': 'Gelöschte ausblenden',
  'admin.accounts.noMatch': 'Kein Konto passt.',
  'admin.accounts.head.name': 'Name',
  'admin.accounts.head.address': 'Adresse',
  'admin.accounts.head.lastSeen': 'Zuletzt online',
  'admin.accounts.head.lastSeenTip':
    'Zuletzt online meint die Sitzung, nie das, was sich jemand angesehen hat.',
  'admin.accounts.head.created': 'Erstellt',
  'admin.accounts.head.sessions': 'Sitzungen',
  'admin.accounts.head.events': 'Ereignisse',
  'admin.accounts.head.keys': 'Schlüssel',
  'admin.accounts.you': 'du',
  'admin.accounts.operator': 'Betreibende',
  'admin.accounts.operatorTip': 'Betreibt diese Instanz: sieht Nutzung und Konten, erstellt Links.',
  'admin.accounts.unverified': 'nicht bestätigt',
  'admin.accounts.unverifiedTip':
    'Adresse noch nicht bestätigt. In seinem Menü lässt sich ein Bestätigungslink erstellen.',
  'admin.accounts.scrubbed': 'bereinigt',
  'admin.accounts.scrubbedTip':
    'Gelöscht, und die persönlichen Daten sind entfernt. Damit lässt sich nichts mehr tun.',
  'admin.accounts.deleted': 'gelöscht',
  'admin.accounts.deletedTip':
    'Gelöscht. Es lässt sich in seinem Menü wiederherstellen, bis die Bereinigung seine persönlichen Daten entfernt.',
  'admin.accounts.scrubOn': 'Bereinigung {date}',
  'admin.accounts.never': 'nie',
  'admin.accounts.acts': 'Aktionen für {name}',
  'admin.account.address': 'Adresse',
  'admin.account.verified': 'Bestätigt',
  'admin.account.operator': 'Betreibende',
  'admin.account.yes': 'ja',
  'admin.account.no': 'nein',
  'admin.account.created': 'Erstellt',
  'admin.account.lastSeen': 'Zuletzt online',
  'admin.account.sessions': 'Sitzungen',
  'admin.account.sessionsValue': '{live} von {count} gültig',
  'admin.account.events': 'Ereignisse',
  'admin.account.eventsValue': '{count} mit der Berechtigung dieser Person',
  'admin.account.keys': 'Agentenschlüssel',
  'admin.account.keysValue': '{count} gültig',
  'admin.account.deleted': 'Gelöscht',
  'admin.account.deletedBySelf': '{date} von der Person selbst',
  'admin.account.deletedBy': '{date} von {name}',
  'admin.account.deletedByOperator': '{date} von einer betreibenden Person',
  'admin.account.scrub': 'Bereinigung',
  'admin.account.scrubDone': 'erfolgt am {date}',
  'admin.account.scrubDue': 'fällig am {date}, außer das Konto wird wiederhergestellt',
  'admin.account.yourAccount': 'dein Konto',
  'admin.account.copyAddress': 'Adresse kopieren',
  'admin.account.restore': 'Wiederherstellen',
  'admin.account.resetLink': 'Link zum Zurücksetzen',
  'admin.account.verifyLink': 'Link zum Bestätigen',
  'admin.account.makeOperator': 'Zu den Betreibenden hinzufügen',
  'admin.account.removeOperator': 'Aus den Betreibenden entfernen',
  'admin.account.delete': 'Löschen',
  'admin.account.copy': 'Kopieren',
  'admin.account.copied': 'Kopiert',
  'admin.account.done': 'OK',
  'admin.account.resetLinkHint':
    'Link zum Zurücksetzen des Passworts. Der Code darin läuft in wenigen Minuten ab; gib ihn jetzt weiter.',
  'admin.account.verifyLinkHint':
    'Link zum Bestätigen der E-Mail-Adresse. Der Code darin läuft in wenigen Minuten ab; gib ihn jetzt weiter.',
  'admin.account.deleteTitle': 'Konto von {name} löschen',
  'admin.account.deleteNow':
    'Jetzt und endgültig beendet: jede Sitzung auf jedem Gerät, jeder Agentenschlüssel und die Benachrichtigungen an jedes Gerät. Eine Wiederherstellung bringt sie nicht zurück; die Person meldet sich neu an und erstellt neue Schlüssel.',
  'admin.account.deleteGrace':
    '{days} Tage lang bringt eine Wiederherstellung das Konto selbst zurück: seinen Namen, seine Adresse, seine Anmeldewege und alles, was es an Aufgaben innehat.',
  'admin.account.deleteScrub':
    'Danach entfernt die Bereinigung die persönlichen Daten, und nichts lässt sich mehr wiederherstellen.',
  'admin.account.deleteCancel': 'Abbrechen',
  'admin.account.deleteConfirm': 'Konto löschen',

  // --- Links ---
  'admin.links.new': 'Neuer Link',
  'admin.links.newHint':
    'Erstellt ein Konto. Mit einer Gruppe wird die neue Person auch dort aufgenommen.',
  'admin.links.label': 'Bezeichnung',
  'admin.links.labelPlaceholder': 'Für wen der Link ist',
  'admin.links.uses': 'Nutzungen',
  'admin.links.expiresInDays': 'Gültigkeit in Tagen',
  'admin.links.seatInGroup': 'In Gruppe aufnehmen',
  'admin.links.noGroup': 'Keine Gruppe (nur Konto)',
  'admin.links.role': 'Rolle',
  'admin.links.groupRole': 'Rolle in der Gruppe',
  'admin.links.create': 'Link erstellen',
  'admin.links.copy': 'Kopieren',
  'admin.links.copied': 'Kopiert',
  'admin.links.done': 'OK',
  'admin.links.shownOnce':
    'Nur einmal gezeigt, für „{label}“. Gespeichert wird nur ein Hash; geht der Link verloren, erstelle einen neuen.',
  'admin.links.filter': 'Links filtern',
  'admin.links.filterPlaceholder': 'Links filtern…',
  'admin.links.showExpired': '{count} abgelaufene zeigen',
  'admin.links.hideExpired': '{count} abgelaufene ausblenden',
  'admin.links.noMatch': 'Kein Link passt.',
  'admin.links.noneOpen': 'Keine offenen Links.',
  'admin.links.head.label': 'Bezeichnung',
  'admin.links.head.seat': 'Gruppe',
  'admin.links.head.uses': 'Nutzungen',
  'admin.links.head.expires': 'Läuft ab',
  'admin.links.head.madeBy': 'Erstellt von',
  'admin.links.head.usedBy': 'Genutzt von',
  'admin.links.empty': 'Noch keine Links.',
  'admin.links.seat': '{group} · {role}',
  'admin.links.accountOnly': 'nur Konto',
  'admin.links.usesTip': {
    one: '{used} von {count} Nutzung verbraucht',
    other: '{used} von {count} Nutzungen verbraucht',
  },
  'admin.links.usedByNames': '{names}',
  'admin.links.removedAccount': 'entferntes Konto',
  'admin.links.acts': 'Aktionen für {label}',
  'admin.links.revoke': 'Widerrufen',
  'admin.links.status.exhausted': 'aufgebraucht',
  'admin.links.status.expired': 'abgelaufen',
  'admin.links.status.revoked': 'widerrufen',
  'admin.links.exhaustedTip': {
    one: 'Die eine Nutzung ist verbraucht; niemand sonst kann über diesen Link beitreten.',
    other: 'Alle {count} Nutzungen sind verbraucht; niemand sonst kann über diesen Link beitreten.',
  },
  'admin.links.expiredTip': 'Abgelaufen ({date}); erstelle einen neuen Link.',
  'admin.links.revokedTip': 'Von einer betreibenden Person widerrufen.',
  'admin.links.revokedOnTip': 'Von einer betreibenden Person am {date} widerrufen.',
} as const satisfies AreaCatalog<typeof en>;
